import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'src/api/axios';
import Header from 'src/layouts/Header';
import Footer from 'src/layouts/Footer';
import BlogCard from 'src/components/blog/BlogCard';
import useSEO from 'src/hooks/useSEO';

// Pomocne funkcije za localStorage
const VIEWED_BLOGS_KEY = 'viewedBlogSlugs';
const MAX_VIEWED_BLOGS = 50; // Maksimalan broj pročitanih blogova koje pamtimo

const getViewedBlogs = () => {
  try {
    const viewed = localStorage.getItem(VIEWED_BLOGS_KEY);
    return viewed ? JSON.parse(viewed) : [];
  } catch {
    return [];
  }
};

const addViewedBlog = (slug) => {
  try {
    let viewed = getViewedBlogs();
    // Ako već postoji, ukloni ga da bismo ga dodali na početak (najnoviji)
    viewed = viewed.filter(s => s !== slug);
    // Dodaj na početak
    viewed.unshift(slug);
    // Ograniči listu na MAX_VIEWED_BLOGS
    if (viewed.length > MAX_VIEWED_BLOGS) {
      viewed = viewed.slice(0, MAX_VIEWED_BLOGS);
    }
    localStorage.setItem(VIEWED_BLOGS_KEY, JSON.stringify(viewed));
  } catch {
    // Ignoriši greške localStorage-a
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestedPosts, setSuggestedPosts] = useState([]);

  // SEO
  const seoData = useMemo(() => {
    if (!post) {
      return {
        title: 'Blog Post - SphynxPlace.com',
        description: 'Read articles and stories about Sphynx cats.',
      };
    }

    const description = post.excerpt || post.content?.replace(/<[^>]*>/g, '').substring(0, 160) || '';
    
    return {
      title: `${post.title} - SphynxPlace.com`,
      description,
      image: post.featured_image || '/build/favicon-96x96.png',
      url: `${window.location.origin}/blog/${post.slug}`,
      type: 'article',
      keywords: 'Sphynx cat blog, cat care tips, Sphynx breed information, cat adoption guides',
      robots: 'index, follow',
      article: {
        published_time: post.created_at,
        section: 'Sphynx Cats',
        tags: ['Sphynx', 'Cat', 'Blog'],
      },
    };
  }, [post]);

  useSEO(seoData);

  // Dodaj trenutni blog u listu pročitanih
  useEffect(() => {
    if (slug) {
      addViewedBlog(slug);
    }
  }, [slug]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/blog/${slug}`);
        const fetchedPost = response.data.data;
        setPost(fetchedPost);
        
        // Redirektuj na tačan slug ako se slug iz API-ja razlikuje od sluga u URL-u
        // Ovo se dešava ako se npr. pristupi preko starog sluga
        if (fetchedPost && fetchedPost.slug && fetchedPost.slug !== slug) {
          navigate(`/blog/${fetchedPost.slug}`, { replace: true });
        }
      } catch (error) {
        console.error('Error fetching blog post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug, navigate]);

  const fetchSuggestedPosts = useCallback(async () => {
    try {
      // Dohvati više postova da bismo imali izbor za filtriranje
      const response = await axios.get(`/api/blog?per_page=20&order_by=random`);
      const allPosts = response.data.data || [];
      
      // Dohvati listu pročitanih blogova
      const viewedSlugs = getViewedBlogs();
      
      // Filtriraj: ukloni trenutni post i već pročitane
      let filteredPosts = allPosts.filter(p => 
        p.slug !== slug && !viewedSlugs.includes(p.slug)
      );
      
      // Ako nemamo dovoljno nepročitanih, dodaj i pročitane (osim trenutnog)
      if (filteredPosts.length < 4) {
        const additionalPosts = allPosts.filter(p => 
          p.slug !== slug && !filteredPosts.some(fp => fp.slug === p.slug)
        );
        filteredPosts = [...filteredPosts, ...additionalPosts];
      }
      
      // Uzmi samo 4 posta
      setSuggestedPosts(filteredPosts.slice(0, 4));
    } catch (error) {
      console.error('Error fetching suggested posts:', error);
    }
  }, [slug]);

  useEffect(() => {
    fetchSuggestedPosts();
  }, [fetchSuggestedPosts]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="container mx-auto px-2 py-8">
          <article className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-12 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-8"></div>
              <div className="h-[400px] bg-gray-200 rounded-lg mb-8"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </div>
          </article>
        </div>
        <div className="animate-pulse bg-[#F7F7F7] py-16 md:py-24">
          <div className="container mx-auto px-2">
            <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-12"></div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-[15px] 2xl:gap-[32px]">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-[300px] bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Header />
        <div className="container mx-auto px-2 py-8">
          <h1 className="text-2xl font-bold">Blog post not found</h1>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container mx-auto px-2 py-8">
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-4">{post.title}</h1>
          <div className="text-gray-600 mb-8">
            <time dateTime={post.created_at}>
              {post.created_at}
            </time>
          </div>
          {post.featured_image && (
            <img 
              src={post.featured_image} 
              alt={post.title}
              className="w-full h-auto rounded-lg mb-8 object-cover"
            />
          )}
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </article>

      </div>
      {suggestedPosts.length > 0 && (
      <div className='bg-[#F7F7F7] py-16 md:py-24'>
        <div className='container mx-auto px-2 flex flex-wrap gap-[15px] 2xl:gap-[32px] justify-center'>
          <h2 className='w-full font-bold text-[25px] sm:text-[35px] pb-10 sm:pb-[60px] text-center uppercase'>
            More articles
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-[15px] 2xl:gap-[32px]">
                {suggestedPosts.map(post => (
                  <BlogCard key={post.id} post={post} />
                ))}
              </div>
        </div>
      </div>
      )}
      <Footer />
    </>
  );
};

export default BlogPost;
